import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { RxComponent } from 'app/core/components/common/rx.component';
import { Action, ActionCreator, ActionTypes, SelectionUtils, SilkTypes } from 'silk-editor';
import { AddImageComponent } from './add-image/add-image.component';
import { Subject } from 'rxjs/Subject';
import { SmartModalService } from 'app/core/services/smart-modal.service';

const { BlockType, MarkType, InlineType } = SilkTypes;
const { TableActionType } = ActionTypes;

declare var $: any;

@Component({
    selector: 'silk-standard-toolbar',
    templateUrl: './standard-toolbar.component.html',
    styleUrls: ['./standard-toolbar.component.scss']
})
export class StandardToolbarComponent extends RxComponent {

    @Input() textFormattingSectionOnly: boolean;

    @Input()
    isInTable$: Observable<boolean>;
    @Input()
    stateChanged$: Observable<any>;
    actionsState$: Observable<any>;
    linkState$: Observable<boolean>;
    fontSize$: Observable<number>;
    imageAddClicked$ = new Subject();

    @Output()
    actionClicked = new EventEmitter<Action>();
    @Output()
    onResetFocus = new EventEmitter();

    marks = [
        { type: MarkType.BOLD, icon: 'format_bold', tooltip: 'Bold' },
        { type: MarkType.ITALIC, icon: 'format_italic', tooltip: 'Italic' },
        { type: MarkType.UNDERLINED, icon: 'format_underlined', tooltip: 'Underline' },
        { type: 'align_left', icon: 'format_align_left', border: true, tooltip: 'Align left' },
        { type: 'align_center', icon: 'format_align_center', tooltip: 'Align center' },
        { type: 'align_right', icon: 'format_align_right', tooltip: 'Align right' },
        { type: 'align_justify', icon: 'format_align_justify', tooltip: 'Justify' },
    ];

    formatMarks = this.marks.slice(0, 3);
    alignMarks = this.marks.slice(3);

    textColorMark = { type: MarkType.COLOR, icon: 'format_color_text', tooltip: 'Text color' };
    fontSizeMark = { type: 'text-size', tooltip: 'Text size' };
    linkMark = { type: 'link', icon: 'fa fa-link', tooltip: 'Insert link' };
    unlinkMark = { type: 'unlink', icon: 'fa fa-unlink', tooltip: 'Remove link' };

    blocks = [
        { type: BlockType.IMAGE, icon: 'fa fa-image', tooltip: 'Image', fa: true },
        { type: BlockType.HEADING_ONE, icon: ['fa fa-header', '1'], fa: true, multi: true, tooltip: 'Header 1' },
        { type: BlockType.HEADING_TWO, icon: ['fa fa-header', '2'], fa: true, multi: true, tooltip: 'Header 2' },
        { type: BlockType.NUMBERED_LIST, icon: 'format_list_numbered', tooltip: 'Ordered List' },
        { type: BlockType.UNORDERED_LIST, icon: 'format_list_bulleted', tooltip: 'Unordered List' },
        { type: BlockType.PAGE_BREAK, icon: 'note_add', tooltip: 'Page Break' },
    ];
    textFormatBlocks = [
        { type: BlockType.HEADING_ONE, icon: ['fa fa-header', '1'], fa: true, multi: true, tooltip: 'Header 1' },
        { type: BlockType.HEADING_TWO, icon: ['fa fa-header', '2'], fa: true, multi: true, tooltip: 'Header 2' },
        { type: BlockType.NUMBERED_LIST, icon: 'format_list_numbered', tooltip: 'Ordered List' },
        { type: BlockType.UNORDERED_LIST, icon: 'format_list_bulleted', tooltip: 'Unordered List' }
    ]

    setCellColorMark = { type: 'set-cell-color', icon: 'format_color_fill', tooltip: 'Fill Cell' };

    mergeCells = { type: TableActionType.MERGE_CELLS, icon: 'fa fa-compress', tooltip: 'Merge Cells' };
    splitCell = { type: TableActionType.SPLIT_CELLS, icon: 'fa fa-expand', tooltip: 'Split Cell' };

    tableButtons = [
        { type: TableActionType.TOGGLE_BORDERS, icon: 'border_clear', tooltip: 'Toggle Borders' },
        { type: TableActionType.ADD_COL, icon: ['fa fa-columns', 'fa fa-plus'], multi: true, tooltip: 'Add Column', fa: true },
        { type: TableActionType.REMOVE_COL, icon: ['fa fa-columns', 'fa fa-minus'], multi: true, tooltip: 'Remove Column', fa: true },
        { type: TableActionType.ADD_ROW, icon: ['fa fa-columns fa-rotate-90', 'fa fa-plus fa-rotate-90 multi-extra-bottom'], fa: true, multi: true, tooltip: 'Add Row' },
        { type: TableActionType.REMOVE_ROW, icon: ['fa fa-columns fa-rotate-90', 'fa fa-minus fa-rotate-180 multi-extra-bottom'], fa: true, multi: true, tooltip: 'Remove Row' },
        { type: TableActionType.REMOVE_TABLE, icon: 'grid_off', tooltip: 'Remove Table' },
    ];

    tableBlock = { type: BlockType.TABLE, icon: 'grid_on', tooltip: 'Table' };

    defaultFonts = {
        [BlockType.HEADING_ONE]: 22,
        [BlockType.HEADING_TWO]: 18,
        [BlockType.PARAGRAPH]: 11
    };

    constructor(private modalService: SmartModalService) {
        super();
    }

    ngOnInit() {
        console.log(this.textFormattingSectionOnly)
        this.actionsState$ = this.stateChanged$
            .withLatestFrom(this.isInTable$)
            .map(([value, isInTable]) => {
                let state = {};
                for (let mark of this.marks) {
                    state[mark.type] = SelectionUtils.hasMark(mark.type, value);
                }

                for (let block of this.blocks) {
                    state[block.type] = SelectionUtils.hasBlock(block.type, value);
                }
                for (let block of this.textFormatBlocks) {
                    state[block.type] = SelectionUtils.hasBlock(block.type, value);
                }

                state[this.linkMark.type] = {
                    active: SelectionUtils.hasInline(this.linkMark.type, value),
                    collectText: value.isEmpty
                };

                state[this.fontSizeMark.type] = {
                    size: this.getFontSize(value)
                };

                state[ActionTypes.TableActionType.TOGGLE_BORDERS] = isInTable && this.isTableWithBorders(value);

                return state;
            })
            .startWith({})
            .shareReplay();

        this.linkState$ = this.actionsState$
            .map(state => state[this.linkMark.type])
            .throttleTime(100)
            .share();

        this.fontSize$ = this.actionsState$
            .map(state => state[this.fontSizeMark.type])
            .share();

        this.stateChanged$
            // .debounceTime(100)
            .bindToLifecycle(this)
            .subscribe(() => {
                $('.silk-a').each(function () {
                    $(this).popover('hide');
                });
                $('.toolbar-container .button').each(function () {
                    $(this).tooltip('destroy');
                });
            });

        this.imageAddClicked$
            .flatMap(_ => this.modalService.open(AddImageComponent, {}, 'Insert Image')
                .map(data => data.result)
                .catchAndEmit({ src: null, key: null, bucket: null })
            )
            .bindToLifecycle(this)
            .subscribe((data: { src, key, bucket }) => {
                if (data.src) {
                    const { src, key, bucket } = data;
                    this.actionClicked.emit(ActionCreator.addImage(src, key, bucket));
                }

                this.resetFocus();
            });
    }

    private getFontSize(value): number {
        const sizeMarks = value.activeMarks.filter(mark => mark.type === MarkType.SIZE);
        if (sizeMarks.size === 1) {
            // A size mark has been applied to the whole selection
            return sizeMarks.first().get('data').get('size');
        }

        const {blocks} = value;
        if (blocks.size === 1) {
            // TODO: check for partially overlapping marks
            const type = blocks.first().type;

            return this.defaultFonts[type] || this.defaultFonts[BlockType.PARAGRAPH];
        } else {
            return undefined;
        }
    }

    ngAfterViewInit() {

        $(() => {

            $('.silk-holder').popover(
                {content: function() {
                    return `<a href='${this.href}' target="_blank">${this.href}</a>`;
                    },
                    placement: 'bottom', container: 'body', selector: '.silk-a', html:true}
            );

            $('.toolbar-container').tooltip({
                title: function() {
                    console.log($(this).attr('title'), 'a')
                    return $(this).attr('title');
                },
                selector: '.button',
                placement: 'top',
                container: 'body'
            });

            $('.silk-holder').on('click', function (e) {
                $('.silk-a').each(function () {
                    // hide any open popovers when the anywhere else in the body is clicked
                    if (!$(this).is(e.target) && $(this).has(e.target).length === 0 && $('.popover').has(e.target).length === 0) {
                        $(this).popover('hide');
                    }
                });
            });
        })
    }

    private isTableWithBorders({ document, blocks }): boolean {
        const node = blocks.first();
        const table = document.getClosest(node.key, parent => parent.type === SilkTypes.BlockType.TABLE);
        return table && node.get('data').get('noBorder');
    }

    toggleMark(e: MouseEvent, markType: string) {
        e.preventDefault();
        this.actionClicked.emit(ActionCreator.toggleMark(markType));
    }

    toggleBlock(e: MouseEvent, blockType: string) {
        e.preventDefault();
        if (blockType === SilkTypes.BlockType.IMAGE) {
            // Special case -> prompt for image
            this.imageAddClicked$.next();
        } else {
            this.actionClicked.emit(ActionCreator.toggleBlock(blockType));
        }
    }

    toggleTableAction(e: MouseEvent, tableAction: string) {
        e.preventDefault();
        this.actionClicked.emit(ActionCreator.toggleTableAction(tableAction));
    }

    addLink({ href, text }) {
        this.actionClicked.emit(ActionCreator.addLink(href, text));
    }

    removeLink(e: MouseEvent) {
        e.preventDefault();
        $('.silk-a').each(function () {
            $(this).popover('hide');
        });
        this.actionClicked.emit(ActionCreator.removeLink());
    }

    onTableSelection({ event: MouseEvent, state: { row, col } }) {
        this.actionClicked.emit(ActionCreator.addTable(row + 1, col + 1));
    }

    setColor(color: string) {
        this.actionClicked.emit(ActionCreator.setTextColor(color));
        this.resetFocus();
    }

    setCellColor(color: string) {
        this.actionClicked.emit(ActionCreator.setCellColor(color));
        this.resetFocus();
    }

    setFontSize(size: number) {
        this.actionClicked.emit(ActionCreator.setTextSize(size));
        this.resetFocus();
    }

    resetFocus() {
        this.onResetFocus.emit();
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/silk/toolbars/standard-toolbar/standard-toolbar.component.ts